import {useEffect} from "react";

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconPrinter = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="1" width="12" height="6" rx="1"/>
        <path d="M4 13H2.5A.5.5 0 0 1 2 12.5v-6A.5.5 0 0 1 2.5 6h15a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-.5.5H16"/>
        <rect x="4" y="11" width="12" height="8" rx="1"/>
        <line x1="7" y1="15" x2="13" y2="15"/>
        <line x1="7" y1="17" x2="11" y2="17"/>
    </svg>
);

const IconX = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round">
        <line x1="4" y1="4" x2="12" y2="12"/>
        <line x1="12" y1="4" x2="4" y2="12"/>
    </svg>
);

// ─── Step data ────────────────────────────────────────────────────────────────

const STEPS = [
    {
        num: "1",
        title: "Scroll to the top of the report",
        desc: "The print dialog captures what's visible. Starting from the top ensures the full report is included.",
    },
    {
        num: "2",
        title: 'Open "More settings" in the print dialog',
        desc: (
            <>
                Look for <Kbd>More settings</Kbd> or <Kbd>Options</Kbd> after clicking Print Preview.
            </>
        ),
    },
    {
        num: "3",
        title: "Adjust these two settings",
        desc: null,
        tips: [
            {
                label: "Headers and footers",
                detail: "Uncheck to remove the browser URL and page title from the printout.",
            },
            {
                label: "Background graphics",
                detail: "Check this to preserve report colours, shading, and chart backgrounds.",
            },
        ],
    },
];

function Kbd({children}) {
    return (
        <span style={{
            display: "inline-block",
            padding: "1px 6px",
            fontSize: "11px",
            fontFamily: "monospace",
            background: "#f1f5f9",
            border: "1px solid #d1d5db",
            borderRadius: "3px",
            color: "#374151",
            verticalAlign: "middle",
            lineHeight: "1.6",
        }}>
            {children}
        </span>
    );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

const PrintGuideModal = ({onCancel, onPrint}) => {

    // Close on Escape
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") onCancel();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [onCancel]);

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onCancel}
                style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.45)",
                    zIndex: 1000,
                    backdropFilter: "blur(2px)",
                }}
            />

            {/* Modal */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="print-modal-title"
                style={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1001,
                    width: "min(440px, calc(100vw - 32px))",
                    backgroundColor: "#ffffff",
                    borderRadius: "10px",
                    boxShadow: "0 20px 48px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                }}
            >
                {/* Header */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "16px 20px",
                    borderBottom: "1px solid #f1f5f9",
                    backgroundColor: "#f8fafc",
                }}>
                    <span style={{color: "#1e3a5f"}}>
                        <IconPrinter/>
                    </span>
                    <span
                        id="print-modal-title"
                        style={{fontSize: "14px", fontWeight: 600, color: "#1e293b", flex: 1}}
                    >
                        Before you print
                    </span>
                    <button
                        onClick={onCancel}
                        aria-label="Close"
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#94a3b8",
                            padding: "2px",
                            display: "flex",
                            alignItems: "center",
                            borderRadius: "4px",
                        }}
                    >
                        <IconX/>
                    </button>
                </div>

                {/* Body */}
                <div style={{padding: "20px"}}>
                    <p style={{margin: "0 0 16px", fontSize: "13px", color: "#64748b", lineHeight: "1.5"}}>
                        Follow these steps for the best printout.
                    </p>

                    <ol style={{
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px"
                    }}>
                        {STEPS.map((step) => (
                            <li key={step.num} style={{display: "flex", gap: "12px"}}>
                                {/* Step number bubble */}
                                <div style={{
                                    flexShrink: 0,
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    backgroundColor: "#1e3a5f",
                                    color: "#ffffff",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginTop: "1px",
                                }}>
                                    {step.num}
                                </div>

                                <div style={{flex: 1}}>
                                    <p style={{
                                        margin: "0 0 4px",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: "#1e293b",
                                        lineHeight: "1.4"
                                    }}>
                                        {step.title}
                                    </p>
                                    {step.desc && (
                                        <p style={{
                                            margin: "0 0 8px",
                                            fontSize: "12px",
                                            color: "#64748b",
                                            lineHeight: "1.5"
                                        }}>
                                            {step.desc}
                                        </p>
                                    )}
                                    {step.tips && (
                                        <div style={{display: "flex", flexDirection: "column", gap: "6px"}}>
                                            {step.tips.map((tip) => (
                                                <div
                                                    key={tip.label}
                                                    style={{
                                                        padding: "8px 10px",
                                                        background: "#f8fafc",
                                                        borderRadius: "6px",
                                                        border: "1px solid #e2e8f0",
                                                    }}
                                                >
                                                    <p style={{
                                                        margin: "0 0 2px",
                                                        fontSize: "12px",
                                                        fontWeight: 600,
                                                        color: "#374151"
                                                    }}>
                                                        {tip.label}
                                                    </p>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: "11px",
                                                        color: "#64748b",
                                                        lineHeight: "1.5"
                                                    }}>
                                                        {tip.detail}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* Footer */}
                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    padding: "14px 20px",
                    borderTop: "1px solid #f1f5f9",
                    backgroundColor: "#f8fafc",
                }}>
                    <button
                        onClick={onCancel}
                        style={{
                            padding: "7px 16px",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#374151",
                            backgroundColor: "#ffffff",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onPrint}
                        style={{
                            padding: "7px 20px",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#ffffff",
                            backgroundColor: "#1e3a5f",
                            border: "1px solid #1e3a5f",
                            borderRadius: "6px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                        }}
                    >
                        <IconPrinter/>
                        Print
                    </button>
                </div>
            </div>
        </>
    );
};

export default PrintGuideModal;